import { Icon } from '@design-system/index'
import { colors, typo } from '@design-system/styles'
import styled from '@emotion/styled'
import { ACHIEVEMENT_LEVEL_COLOR, ACHIEVEMENT_LEVEL_KO } from '@mathflat/domain/Achievement'
import clsx from 'clsx'
import { nanoid } from 'nanoid'
import { useMemo } from 'react'

import type { AchievementConceptChip } from '@/dto/AchievementAnalysis/AchievementConceptChip.dto'

type Props = {
  checked: boolean
  status: AchievementConceptChip['achievementLevel']
  star?: boolean
  onChange?: (checked: boolean) => void
  size?: number
  iconSize?: number
  disabled?: boolean
  withKoLevelText?: boolean
  radius?: 'small' | 'medium'
  'data-testid'?: string
}

const AchievementChip = ({
  checked,
  star,
  status,
  onChange,
  size = 34,
  iconSize = 26,
  withKoLevelText = false,
  disabled = false,
  radius = 'medium',
  'data-testid': dataTestId,
}: Props) => {
  const id = useMemo(() => nanoid(), [])

  const backgroundColor = ACHIEVEMENT_LEVEL_COLOR[status]

  return (
    <S.Container
      className={clsx((!disabled && onChange) || 'default-cursor')}
      data-testid={dataTestId}
    >
      <S.Input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={(e) => {
          if (disabled) return
          onChange?.(e.target.checked)
        }}
      />
      <S.Label
        htmlFor={id}
        className={radius}
        data-testid={dataTestId ? `${dataTestId}-label` : undefined}
        style={{ backgroundColor, width: size, height: size }}
      >
        {status === 'SAD' && (
          <Icon name="achievement_analysis_sad" size={iconSize} className="no-fill-current-color" />
        )}
        {status === 'SMILE' && (
          <Icon
            name="achievement_analysis_smile"
            size={iconSize}
            className="no-fill-current-color"
          />
        )}
        {checked && (
          <S.CheckedWrap>
            <Icon name="check" color="#fff" size={iconSize} />
          </S.CheckedWrap>
        )}
      </S.Label>
      {star && <Icon name="star" className="top-right-icon" size={10} color={colors.gray.$500} />}
      {withKoLevelText && <span className="kr-level-text">{ACHIEVEMENT_LEVEL_KO[status]}</span>}
    </S.Container>
  )
}

const S = {
  Container: styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    &.default-cursor label {
      cursor: default;
    }

    > .top-right-icon {
      position: absolute;
      top: -4px;
      right: -2px;
    }

    > .kr-level-text {
      ${typo.caption01};
      color: var(--Colors-Gray-900);
      font-weight: 500;
      margin-left: 4px;
    }
  `,

  Input: styled.input`
    display: none;
  `,
  Label: styled.label`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid ${colors.gray.$300};
    border-radius: var(--Radius-200);
    color: ${colors.gray.$700};
    background-color: white;
    white-space: pre;
    cursor: pointer;

    &.small {
      border-radius: var(--Radius-100);
    }
  `,
  CheckedWrap: styled.div`
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    border-radius: var(--Radius-100);
    background: rgba(0, 0, 0, 0.5);
  `,
}

export default AchievementChip
